import { Rectangle, Texture, TextureSource } from 'pixi.js';
import { MOBS, PETS, type HeroId } from '@knight/shared';

/**
 * Painterly silhouette art, drawn with Canvas 2D at 2x resolution.
 * Style: ink-dark bodies, coloured rim light, glowing accents, tattered edges.
 */
export const RES = 2;

type Ctx = CanvasRenderingContext2D;
const cache = new Map<string, Texture>();
const canvasCache = new Map<string, HTMLCanvasElement>();

export interface Palette {
  base: string;
  dark: string;
  light: string;
  accent: string;
  glow: string;
}

export function shadeHex(hex: string, f: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 255) * f)));
  const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 255) * f)));
  const b = Math.max(0, Math.min(255, Math.round((n & 255) * f)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

export function palette(base: string, accent: string): Palette {
  return { base, dark: shadeHex(base, 0.55), light: shadeHex(base, 1.45), accent, glow: accent };
}

function makeCanvas(key: string, w: number, h: number, draw: (ctx: Ctx, w: number, h: number) => void): HTMLCanvasElement {
  const hit = canvasCache.get(key);
  if (hit) return hit;
  const c = document.createElement('canvas');
  c.width = Math.ceil(w * RES);
  c.height = Math.ceil(h * RES);
  const ctx = c.getContext('2d')!;
  ctx.scale(RES, RES);
  draw(ctx, w, h);
  canvasCache.set(key, c);
  return c;
}

export function paint(key: string, w: number, h: number, draw: (ctx: Ctx, w: number, h: number) => void): Texture {
  const hit = cache.get(key);
  if (hit) return hit;
  const c = makeCanvas(key, w, h, draw);
  const tex = Texture.from(c);
  tex.source.scaleMode = 'linear';
  cache.set(key, tex);
  return tex;
}

// ------------------------------------------------------------------ drawing helpers

function glow(ctx: Ctx, color: string, blur: number): void {
  ctx.shadowColor = color;
  ctx.shadowBlur = blur * RES;
}

function noGlow(ctx: Ctx): void {
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';
}

function vgrad(ctx: Ctx, x: number, y0: number, y1: number, c0: string, c1: string): CanvasGradient {
  const g = ctx.createLinearGradient(x, y0, x, y1);
  g.addColorStop(0, c0);
  g.addColorStop(1, c1);
  return g;
}

function eye(ctx: Ctx, x: number, y: number, r: number, color: string): void {
  glow(ctx, color, 6);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();
  noGlow(ctx);
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(x, y, r * 0.35, 0, Math.PI * 2);
  ctx.fill();
}

/** Tattered cloak silhouette from shoulders to hem. */
function cloakPath(ctx: Ctx, w: number, top: number, bottom: number, shoulder: number, hem: number, jag: number): void {
  const cx = w / 2;
  ctx.beginPath();
  ctx.moveTo(cx - shoulder, top);
  ctx.quadraticCurveTo(cx - shoulder * 1.15, (top + bottom) / 2, cx - hem, bottom);
  const steps = 6;
  for (let i = 0; i <= steps; i++) {
    const x = cx - hem + (hem * 2 * i) / steps;
    const y = bottom - (i % 2 === 0 ? 0 : jag) - (i === 0 || i === steps ? jag * 0.5 : 0);
    ctx.lineTo(x, y);
  }
  ctx.quadraticCurveTo(cx + shoulder * 1.15, (top + bottom) / 2, cx + shoulder, top);
  ctx.quadraticCurveTo(cx, top - shoulder * 0.35, cx - shoulder, top);
  ctx.closePath();
}

function rim(ctx: Ctx, color: string, width = 1.4, alpha = 0.6): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
  ctx.restore();
}

// ------------------------------------------------------------------ unit parts

export type PartKind = 'legL' | 'legR' | 'body' | 'head' | 'weapon' | 'wingL' | 'wingR' | 'aura' | 'tail' | 'arm';

export interface PartSpec {
  kind: PartKind;
  tex: Texture;
  /** logical offset of the part's pivot from the unit's feet point */
  x: number;
  y: number;
  /** anchor within the texture (0..1) */
  ax: number;
  ay: number;
  /** draw order */
  z: number;
  alpha?: number;
  additive?: boolean;
}

export interface UnitArt {
  parts: PartSpec[];
  /** logical height in px (feet to top), after `scale` */
  height: number;
  family: 'walker' | 'flyer' | 'blob' | 'spirit' | 'static' | 'beast';
  /** uniform scale applied to every part (1 = texture size) */
  scale?: number;
}

/** Heroes are drawn a touch smaller than their textures so they read as agile next to the mobs. */
export const HERO_SCALE = 0.88;

const HERO_STYLE: Record<HeroId, { base: string; accent: string; cloth: string }> = {
  revenant: { base: '#1c1a2a', accent: '#9d8cff', cloth: '#2e2a44' },
  pilgrim: { base: '#241e1a', accent: '#ffd27a', cloth: '#4a3a2c' },
  hexer: { base: '#1a1220', accent: '#d97aff', cloth: '#3a2244' },
  druid: { base: '#1c2418', accent: '#8fe38a', cloth: '#2e4a2c' },
  warden: { base: '#1a1e28', accent: '#9fd3ff', cloth: '#2c3a4a' },
  bard: { base: '#241a26', accent: '#ffd166', cloth: '#4a2c4a' },
  leomorph: { base: '#2a1a10', accent: '#ffb347', cloth: '#8a4a1a' },
  mystic: { base: '#1c1020', accent: '#ff6ad5', cloth: '#3a1c3e' },
  shaman: { base: '#22160f', accent: '#ff8c42', cloth: '#4a2e1c' },
  goblins: { base: '#14200f', accent: '#a7ff5c', cloth: '#3a4a2a' },
};

// ------------------------------------------------------------------ parts of the unlockable heroes

function halberdTex(key: string, p: Palette, w = 16, h = 56): Texture {
  return paint('halberd:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.strokeStyle = vgrad(ctx, 0, 0, h, '#6a5a4a', '#2a2018');
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx, 6);
    ctx.lineTo(cx, h - 1);
    ctx.stroke();
    glow(ctx, p.accent, 4);
    ctx.fillStyle = vgrad(ctx, 0, 0, 20, '#e8ecf4', '#8a94a8');
    ctx.beginPath();
    ctx.moveTo(cx, 0);
    ctx.lineTo(cx + 2, 8);
    ctx.lineTo(cx - 2, 8);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx + 1, 6);
    ctx.quadraticCurveTo(cx + 9, 8, cx + 8, 20);
    ctx.lineTo(cx + 1, 18);
    ctx.closePath();
    ctx.fill();
    noGlow(ctx);
    ctx.fillStyle = '#8a94a8';
    ctx.beginPath();
    ctx.moveTo(cx - 1, 9);
    ctx.lineTo(cx - 6, 12);
    ctx.lineTo(cx - 1, 15);
    ctx.closePath();
    ctx.fill();
  });
}

function shieldTex(key: string, p: Palette, w = 18, h = 24): Texture {
  return paint('shield:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(p.base, 1.9), p.dark);
    ctx.beginPath();
    ctx.moveTo(1, 2);
    ctx.lineTo(w - 1, 2);
    ctx.quadraticCurveTo(w - 1, h * 0.6, cx, h - 1);
    ctx.quadraticCurveTo(1, h * 0.6, 1, 2);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.4, 0.7);
    glow(ctx, p.accent, 5);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.arc(cx, h * 0.42, 2.2, 0, Math.PI * 2);
    ctx.fill();
    noGlow(ctx);
  });
}

function glowTex(key: string, color: string, r = 24): Texture {
  return paint('glow:' + key, r * 2, r * 2, (ctx) => {
    const g = ctx.createRadialGradient(r, r, 1, r, r, r);
    g.addColorStop(0, color);
    g.addColorStop(0.5, shadeHex(color, 0.6));
    g.addColorStop(1, '#000000');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.fill();
  });
}

function furTorsoTex(key: string, p: Palette, fur: string, w = 38, h = 34): Texture {
  return paint('fur:' + key, w, h, (ctx) => {
    const cx = w / 2;
    const torso = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 17, 6);
      ctx.quadraticCurveTo(cx, 0, cx + 17, 6);
      ctx.lineTo(cx + 12, h - 2);
      ctx.lineTo(cx - 12, h - 2);
      ctx.closePath();
    };
    ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(fur, 1.2), shadeHex(fur, 0.55));
    torso();
    ctx.fill();
    ctx.strokeStyle = 'rgba(20,10,5,0.75)';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    for (let i = 0; i < 4; i++) {
      const y = 8 + i * 6;
      ctx.beginPath();
      ctx.moveTo(cx - 14 + i, y);
      ctx.quadraticCurveTo(cx - 6, y + 3, cx - 3, y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx + 14 - i, y + 2);
      ctx.quadraticCurveTo(cx + 6, y + 5, cx + 3, y + 2);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(255,240,220,0.35)';
    ctx.beginPath();
    ctx.ellipse(cx, h * 0.55, 6, 9, 0, 0, Math.PI * 2);
    ctx.fill();
    torso();
    rim(ctx, p.accent, 1.4, 0.5);
  });
}

function tigerHeadTex(key: string, p: Palette, fur: string, w = 26, h = 24): Texture {
  return paint('tiger:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(fur, 1.25), shadeHex(fur, 0.7));
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(cx + s * 4, 8);
      ctx.lineTo(cx + s * 10, 0);
      ctx.lineTo(cx + s * 11, 9);
      ctx.closePath();
      ctx.fill();
    }
    ctx.beginPath();
    ctx.ellipse(cx, 13, 11, 10, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,240,220,0.5)';
    ctx.beginPath();
    ctx.ellipse(cx + 3, 17, 6, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(20,10,5,0.8)';
    ctx.lineWidth = 1.8;
    ctx.lineCap = 'round';
    for (const [x0, y0, x1, y1] of [
      [cx - 8, 6, cx - 5, 10],
      [cx - 3, 4, cx - 2, 8],
      [cx + 2, 4, cx + 3, 8],
      [cx + 8, 6, cx + 6, 10],
    ]) {
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    }
    ctx.fillStyle = '#2a1410';
    ctx.beginPath();
    ctx.moveTo(cx + 6, 16);
    ctx.lineTo(cx + 9, 16);
    ctx.lineTo(cx + 7.5, 18.5);
    ctx.closePath();
    ctx.fill();
    eye(ctx, cx - 3, 12, 1.5, p.accent);
    eye(ctx, cx + 4, 11.5, 1.5, p.accent);
    ctx.beginPath();
    ctx.ellipse(cx, 13, 11, 10, 0, 0, Math.PI * 2);
    rim(ctx, p.accent, 1.2, 0.35);
  });
}

function clawsTex(key: string, p: Palette, w = 14, h = 16): Texture {
  return paint('claws:' + key, w, h, (ctx) => {
    glow(ctx, p.accent, 3);
    ctx.fillStyle = '#e8ecf4';
    for (let i = 0; i < 3; i++) {
      const x = 3 + i * 4;
      ctx.beginPath();
      ctx.moveTo(x, 6);
      ctx.quadraticCurveTo(x + 4, 8, x + 2, h);
      ctx.quadraticCurveTo(x, 10, x - 1, 7);
      ctx.closePath();
      ctx.fill();
    }
    noGlow(ctx);
    ctx.fillStyle = p.dark;
    ctx.fillRect(1, 2, w - 2, 5);
  });
}

function tailTex(key: string, fur: string, w = 24, h = 20): Texture {
  return paint('tail:' + key, w, h, (ctx) => {
    const path = () => {
      ctx.beginPath();
      ctx.moveTo(w - 2, h - 2);
      ctx.quadraticCurveTo(w * 0.3, h - 2, 4, 4);
    };
    ctx.lineCap = 'round';
    ctx.strokeStyle = fur;
    ctx.lineWidth = 4;
    path();
    ctx.stroke();
    ctx.strokeStyle = 'rgba(20,10,5,0.7)';
    ctx.setLineDash([3, 4]);
    path();
    ctx.stroke();
    ctx.setLineDash([]);
  });
}

function antlerHoodTex(key: string, p: Palette, cloth: string, w = 30, h = 30): Texture {
  return paint('antler:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.strokeStyle = '#d8c8a0';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(cx + s * 4, 12);
      ctx.quadraticCurveTo(cx + s * 9, 6, cx + s * 8, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx + s * 7, 6);
      ctx.lineTo(cx + s * 12, 3);
      ctx.stroke();
    }
    ctx.fillStyle = vgrad(ctx, 0, 8, h, shadeHex(cloth, 1.3), shadeHex(cloth, 0.6));
    ctx.beginPath();
    ctx.moveTo(cx - 9, h);
    ctx.quadraticCurveTo(cx - 10, 14, cx - 1, 9);
    ctx.quadraticCurveTo(cx + 10, 13, cx + 9, h);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.45);
    ctx.fillStyle = '#07060b';
    ctx.beginPath();
    ctx.ellipse(cx, 21, 5.5, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    eye(ctx, cx - 2.4, 21, 1.3, p.accent);
    eye(ctx, cx + 2.4, 21, 1.3, p.accent);
  });
}

function sickleTex(key: string, p: Palette, w = 20, h = 30): Texture {
  return paint('sickle:' + key, w, h, (ctx) => {
    const cx = w / 2 - 2;
    ctx.strokeStyle = vgrad(ctx, 0, 10, h, '#6a5a4a', '#2a2018');
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx, h - 1);
    ctx.lineTo(cx, 12);
    ctx.stroke();
    glow(ctx, p.accent, 4);
    ctx.fillStyle = vgrad(ctx, 0, 0, 18, '#e8ecf4', '#8a94a8');
    ctx.beginPath();
    ctx.arc(cx + 3, 10, 8.5, Math.PI * 1.05, Math.PI * 2.15);
    ctx.arc(cx + 3, 10, 5, Math.PI * 2.15, Math.PI * 1.05, true);
    ctx.closePath();
    ctx.fill();
    noGlow(ctx);
  });
}

function maskTex(key: string, p: Palette, w = 24, h = 26): Texture {
  return paint('mask:' + key, w, h, (ctx) => {
    const cx = w / 2;
    // feathers
    ctx.lineCap = 'round';
    ctx.lineWidth = 2.2;
    for (const [dx, col] of [
      [-5, p.accent],
      [0, shadeHex(p.accent, 1.3)],
      [5, '#e8dcc0'],
    ] as [number, string][]) {
      ctx.strokeStyle = col;
      ctx.beginPath();
      ctx.moveTo(cx + dx * 0.5, 9);
      ctx.quadraticCurveTo(cx + dx * 1.4, 4, cx + dx * 1.8, 0);
      ctx.stroke();
    }
    // wooden mask
    ctx.fillStyle = vgrad(ctx, 0, 6, h, '#8a6a3a', '#4a3418');
    ctx.beginPath();
    ctx.moveTo(cx - 8, 8);
    ctx.lineTo(cx + 8, 8);
    ctx.quadraticCurveTo(cx + 9, h - 4, cx, h - 1);
    ctx.quadraticCurveTo(cx - 9, h - 4, cx - 8, 8);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.5);
    // painted stripes
    ctx.strokeStyle = p.accent;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(cx - 6, 11);
    ctx.lineTo(cx + 6, 11);
    ctx.moveTo(cx, 12);
    ctx.lineTo(cx, h - 4);
    ctx.stroke();
    ctx.fillStyle = '#05040a';
    ctx.fillRect(cx - 6.5, 13, 5, 3);
    ctx.fillRect(cx + 1.5, 13, 5, 3);
    eye(ctx, cx - 4, 14.5, 1.3, p.accent);
    eye(ctx, cx + 4, 14.5, 1.3, p.accent);
  });
}

function goblinHeadTex(key: string, p: Palette, w = 20, h = 18): Texture {
  return paint('gob:' + key, w, h, (ctx) => {
    const cx = w / 2;
    const skin = '#5a8a3a';
    ctx.fillStyle = vgrad(ctx, 0, 2, h, shadeHex(skin, 1.3), shadeHex(skin, 0.6));
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(cx + s * 5, 9);
      ctx.lineTo(cx + s * 10, 4);
      ctx.lineTo(cx + s * 6, 12);
      ctx.closePath();
      ctx.fill();
    }
    ctx.beginPath();
    ctx.ellipse(cx, 10, 6.5, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    rim(ctx, p.accent, 1, 0.4);
    // goggles
    ctx.fillStyle = '#2a2230';
    ctx.fillRect(cx - 6, 7, 12, 4.5);
    glow(ctx, p.accent, 3);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.arc(cx - 2.8, 9.2, 1.7, 0, Math.PI * 2);
    ctx.arc(cx + 2.8, 9.2, 1.7, 0, Math.PI * 2);
    ctx.fill();
    noGlow(ctx);
    // grin
    ctx.strokeStyle = '#1a2410';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - 3, 14);
    ctx.quadraticCurveTo(cx, 16, cx + 3, 14);
    ctx.stroke();
  });
}

function sparkgunTex(key: string, p: Palette, w = 22, h = 10): Texture {
  return paint('sparkgun:' + key, w, h, (ctx) => {
    ctx.fillStyle = vgrad(ctx, 0, 0, h, '#5a5a6a', '#2a2a34');
    ctx.fillRect(2, 3, w - 6, 3.5);
    ctx.fillRect(4, 6, 4, 4);
    ctx.fillStyle = '#7a6a4a';
    ctx.fillRect(9, 5, 6, 2.5);
    glow(ctx, p.accent, 5);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.arc(w - 3, 4.7, 2, 0, Math.PI * 2);
    ctx.fill();
    noGlow(ctx);
  });
}

function capTex(key: string, p: Palette, cloth: string, w = 26, h = 22): Texture {
  return paint('cap:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = '#07060b';
    ctx.beginPath();
    ctx.ellipse(cx, 14, 6, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();
    eye(ctx, cx - 2.6, 14, 1.4, p.accent);
    eye(ctx, cx + 2.6, 14, 1.4, p.accent);
    // feather
    ctx.strokeStyle = p.accent;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx + 3, 7);
    ctx.quadraticCurveTo(cx + 9, 2, cx + 12, 0);
    ctx.stroke();
    // cap
    ctx.fillStyle = vgrad(ctx, 0, 2, 12, shadeHex(cloth, 1.35), shadeHex(cloth, 0.7));
    ctx.beginPath();
    ctx.moveTo(cx - 9, 10);
    ctx.quadraticCurveTo(cx - 8, 1, cx + 1, 2);
    ctx.quadraticCurveTo(cx + 9, 3, cx + 8, 10);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.5);
    ctx.fillStyle = shadeHex(cloth, 0.6);
    ctx.fillRect(cx - 10, 9, 20, 2.2);
  });
}

function luteTex(key: string, p: Palette, w = 16, h = 36): Texture {
  return paint('lute:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = vgrad(ctx, 0, 0, 20, '#5a4a3a', '#2a2018');
    ctx.fillRect(cx - 1.6, 0, 3.2, 20);
    ctx.fillStyle = vgrad(ctx, 0, 14, h, '#c8965a', '#7a4e26');
    ctx.beginPath();
    ctx.ellipse(cx, h - 9, 7, 9, 0, 0, Math.PI * 2);
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.5);
    ctx.fillStyle = '#1a1008';
    ctx.beginPath();
    ctx.arc(cx, h - 10, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,240,200,0.8)';
    ctx.lineWidth = 0.6;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(cx + i * 1.1, 1);
      ctx.lineTo(cx + i * 1.6, h - 4);
      ctx.stroke();
    }
  });
}

function legTex(key: string, p: Palette, w = 8, h = 16): Texture {
  return paint('leg:' + key, w, h, (ctx) => {
    ctx.fillStyle = vgrad(ctx, 0, 0, h, p.base, p.dark);
    ctx.beginPath();
    ctx.moveTo(w * 0.25, 0);
    ctx.lineTo(w * 0.75, 0);
    ctx.lineTo(w * 0.8, h * 0.75);
    ctx.lineTo(w, h * 0.95);
    ctx.lineTo(w, h);
    ctx.lineTo(w * 0.1, h);
    ctx.lineTo(w * 0.15, h * 0.8);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.light, 1, 0.35);
  });
}

function cloakTex(key: string, p: Palette, cloth: string, w: number, h: number, shoulder: number, hem: number): Texture {
  return paint('cloak:' + key, w, h, (ctx) => {
    cloakPath(ctx, w, 4, h - 2, shoulder, hem, 4);
    ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(cloth, 1.15), shadeHex(cloth, 0.45));
    ctx.fill();
    rim(ctx, p.accent, 1.6, 0.55);
    // folds
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 1.2;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(w / 2 + i * shoulder * 0.45, 10);
      ctx.quadraticCurveTo(w / 2 + i * shoulder * 0.7, h * 0.6, w / 2 + i * hem * 0.6, h - 4);
      ctx.stroke();
    }
    // chest plate / rune
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.beginPath();
    ctx.ellipse(w / 2, h * 0.38, shoulder * 0.5, h * 0.14, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

function helmTex(key: string, p: Palette, horns: boolean, w = 22, h = 22): Texture {
  return paint('helm:' + key, w, h, (ctx) => {
    const cx = w / 2;
    if (horns) {
      ctx.fillStyle = shadeHex(p.base, 1.8);
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(cx + s * 5, 9);
        ctx.quadraticCurveTo(cx + s * 12, 6, cx + s * 9, 0);
        ctx.quadraticCurveTo(cx + s * 9, 6, cx + s * 4, 12);
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.fillStyle = vgrad(ctx, 0, 4, h, shadeHex(p.base, 1.9), p.dark);
    ctx.beginPath();
    ctx.moveTo(cx - 7, h - 2);
    ctx.lineTo(cx - 8, 10);
    ctx.quadraticCurveTo(cx, 2, cx + 8, 10);
    ctx.lineTo(cx + 7, h - 2);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.light, 1.2, 0.5);
    // visor
    ctx.fillStyle = '#05040a';
    ctx.fillRect(cx - 6, 11, 12, 4);
    eye(ctx, cx - 3, 13, 1.6, p.accent);
    eye(ctx, cx + 3, 13, 1.6, p.accent);
  });
}

function hoodTex(key: string, p: Palette, cloth: string, w = 22, h = 22): Texture {
  return paint('hood:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(cloth, 1.3), shadeHex(cloth, 0.6));
    ctx.beginPath();
    ctx.moveTo(cx - 9, h);
    ctx.quadraticCurveTo(cx - 10, 6, cx - 1, 1);
    ctx.quadraticCurveTo(cx + 10, 5, cx + 9, h);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.45);
    // shadowed face
    ctx.fillStyle = '#07060b';
    ctx.beginPath();
    ctx.ellipse(cx, 13, 5.5, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    eye(ctx, cx - 2.4, 13, 1.3, p.accent);
    eye(ctx, cx + 2.4, 13, 1.3, p.accent);
  });
}

function hatTex(key: string, p: Palette, cloth: string, w = 34, h = 26): Texture {
  return paint('hat:' + key, w, h, (ctx) => {
    const cx = w / 2;
    // face
    ctx.fillStyle = '#07060b';
    ctx.beginPath();
    ctx.ellipse(cx, 20, 6, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    eye(ctx, cx - 2.6, 20, 1.4, p.accent);
    eye(ctx, cx + 2.6, 20, 1.4, p.accent);
    // brim
    ctx.fillStyle = vgrad(ctx, 0, 12, 20, shadeHex(cloth, 1.2), shadeHex(cloth, 0.5));
    ctx.beginPath();
    ctx.ellipse(cx, 16, 16, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.45);
    // cone, bent backwards (the figure faces +x)
    ctx.fillStyle = vgrad(ctx, 0, 0, 16, shadeHex(cloth, 1.35), shadeHex(cloth, 0.7));
    ctx.beginPath();
    ctx.moveTo(cx + 7, 16);
    ctx.quadraticCurveTo(cx + 4, 6, cx - 6, 0);
    ctx.quadraticCurveTo(cx - 3, 8, cx - 7, 16);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1, 0.4);
    // band
    ctx.fillStyle = p.accent;
    ctx.globalAlpha = 0.6;
    ctx.fillRect(cx - 6, 13, 12, 1.5);
    ctx.globalAlpha = 1;
  });
}

function claymoreTex(key: string, p: Palette, w = 12, h = 46): Texture {
  return paint('claymore:' + key, w, h, (ctx) => {
    const cx = w / 2;
    glow(ctx, p.accent, 4);
    ctx.fillStyle = vgrad(ctx, 0, 0, h * 0.7, '#e8ecf4', '#8a94a8');
    ctx.beginPath();
    ctx.moveTo(cx, 0);
    ctx.lineTo(cx + 3.2, 4);
    ctx.lineTo(cx + 2.6, h * 0.68);
    ctx.lineTo(cx - 2.6, h * 0.68);
    ctx.lineTo(cx - 3.2, 4);
    ctx.closePath();
    ctx.fill();
    noGlow(ctx);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(cx - 0.6, 3);
    ctx.lineTo(cx - 0.6, h * 0.64);
    ctx.stroke();
    // guard & grip
    ctx.fillStyle = p.accent;
    ctx.fillRect(cx - 5.5, h * 0.68, 11, 2.4);
    ctx.fillStyle = '#2a2230';
    ctx.fillRect(cx - 1.4, h * 0.72, 2.8, h * 0.22);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.arc(cx, h * 0.96, 2, 0, Math.PI * 2);
    ctx.fill();
  });
}

function bowTex(key: string, p: Palette, w = 14, h = 42): Texture {
  return paint('bow:' + key, w, h, (ctx) => {
    ctx.strokeStyle = vgrad(ctx, 0, 0, h, '#8a5a2a', '#4a2e18');
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(w * 0.3, 1);
    ctx.quadraticCurveTo(w + 4, h / 2, w * 0.3, h - 1);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,240,200,0.8)';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(w * 0.3, 1);
    ctx.lineTo(w * 0.3, h - 1);
    ctx.stroke();
    glow(ctx, p.accent, 3);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.arc(w * 0.75, h / 2, 1.6, 0, Math.PI * 2);
    ctx.fill();
    noGlow(ctx);
  });
}

function staffTex(key: string, p: Palette, w = 16, h = 50): Texture {
  return paint('staff:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.strokeStyle = vgrad(ctx, 0, 0, h, '#5a4a3a', '#2a2018');
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx, 10);
    ctx.quadraticCurveTo(cx + 1.5, h * 0.6, cx - 0.5, h - 1);
    ctx.stroke();
    // claw around the orb
    ctx.strokeStyle = '#6a5a4a';
    ctx.lineWidth = 1.4;
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(cx, 12);
      ctx.quadraticCurveTo(cx + s * 6, 8, cx + s * 3, 3);
      ctx.stroke();
    }
    glow(ctx, p.accent, 8);
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.arc(cx, 7, 3.6, 0, Math.PI * 2);
    ctx.fill();
    noGlow(ctx);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(cx - 1, 6, 1.2, 0, Math.PI * 2);
    ctx.fill();
  });
}

/** Hero figure; `form` swaps the Leomorph for a beast while a transformation is active. */
export function buildHero(hero: HeroId, ghost = false, form = ''): UnitArt {
  const st = HERO_STYLE[hero] ?? HERO_STYLE.revenant;
  const base = ghost ? '#14101c' : st.base;
  const cloth = ghost ? '#2a2038' : st.cloth;
  const p = palette(base, st.accent);
  const key = hero + (ghost ? ':g' : '');
  const parts: PartSpec[] = [];
  if (hero === 'leomorph' && (form === 'tiger' || form === 'bear')) {
    const fp = form === 'tiger' ? palette(ghost ? '#3a2a20' : '#c8752a', '#ffb347') : palette(ghost ? '#2a2018' : '#5a3a22', '#b07a4a');
    const w = form === 'tiger' ? 62 : 70;
    const h = form === 'tiger' ? 36 : 44;
    parts.push({ kind: 'body', tex: beastTex(key + ':' + form, fp, w, h, form === 'tiger'), x: 0, y: 0, ax: 0.5, ay: 1, z: 2 });
    return { parts, height: h, family: 'beast' };
  }
  if (hero === 'goblins') {
    // three small figures that move as one hero
    const leg = legTex(key, p, 6, 10);
    for (const [i, dx, dy] of [
      [0, -13, 2],
      [1, 12, 1],
      [2, 0, 0],
    ] as [number, number, number][]) {
      parts.push({ kind: 'legL', tex: leg, x: dx - 3, y: -8 + dy, ax: 0.5, ay: 0.05, z: 1 + i * 0.1 });
      parts.push({ kind: 'legR', tex: leg, x: dx + 3, y: -8 + dy, ax: 0.5, ay: 0.05, z: 1 + i * 0.1 });
      parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 18, 16, 7, 8), x: dx, y: -7 + dy, ax: 0.5, ay: 1, z: 2 + i * 0.1 });
      parts.push({ kind: 'head', tex: goblinHeadTex(key, p), x: dx, y: -21 + dy, ax: 0.5, ay: 0.95, z: 3 + i * 0.1 });
    }
    parts.push({ kind: 'weapon', tex: sparkgunTex(key, p), x: 8, y: -13, ax: 0.2, ay: 0.5, z: 4 });
    return { parts, height: 38, family: 'walker' };
  }
  const leg = legTex(key, p);
  parts.push({ kind: 'legL', tex: leg, x: -4, y: -14, ax: 0.5, ay: 0.05, z: 1 });
  parts.push({ kind: 'legR', tex: leg, x: 4, y: -14, ax: 0.5, ay: 0.05, z: 1 });
  if (hero === 'warden') {
    parts.push({ kind: 'arm', tex: shieldTex(key, p), x: -11, y: -30, ax: 0.5, ay: 0.5, z: 1.5 });
    parts.push({ kind: 'weapon', tex: halberdTex(key, p), x: 12, y: -26, ax: 0.5, ay: 0.7, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 42, 34, 16, 13), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: helmTex(key, p, false), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(62 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'mystic') {
    parts.push({ kind: 'aura', tex: glowTex(key, st.accent, 22), x: 0, y: -26, ax: 0.5, ay: 0.5, z: 0, alpha: 0.35, additive: true });
    parts.push({ kind: 'weapon', tex: staffTex(key, p, 16, 46), x: 12, y: -18, ax: 0.5, ay: 0.85, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 32, 36, 11, 12), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: hoodTex(key, p, cloth), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(62 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'leomorph') {
    const fur = ghost ? '#3a2a20' : '#c8752a';
    parts.push({ kind: 'tail', tex: tailTex(key, fur), x: -12, y: -14, ax: 1, ay: 1, z: 1 });
    parts.push({ kind: 'weapon', tex: clawsTex(key, p), x: 12, y: -16, ax: 0.5, ay: 0.2, z: 4 });
    parts.push({ kind: 'body', tex: furTorsoTex(key, p, fur), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: tigerHeadTex(key, p, fur), x: 0, y: -44, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(66 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'druid') {
    parts.push({ kind: 'weapon', tex: sickleTex(key, p), x: 11, y: -24, ax: 0.5, ay: 0.8, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 34, 36, 12, 13), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: antlerHoodTex(key, p, cloth), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(70 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'shaman') {
    parts.push({ kind: 'weapon', tex: staffTex(key, p), x: 12, y: -18, ax: 0.5, ay: 0.85, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 34, 36, 12, 13), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: maskTex(key, p), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(66 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'bard') {
    parts.push({ kind: 'weapon', tex: luteTex(key, p), x: 11, y: -24, ax: 0.5, ay: 0.6, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 32, 34, 11, 11), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: capTex(key, p, cloth), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(62 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'revenant') {
    parts.push({ kind: 'weapon', tex: claymoreTex(key, p), x: 11, y: -22, ax: 0.5, ay: 0.75, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 40, 34, 15, 13), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: helmTex(key, p, true), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(62 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  if (hero === 'pilgrim') {
    // bow held forward, in the facing direction
    parts.push({ kind: 'weapon', tex: bowTex(key, p), x: 10, y: -30, ax: 0.35, ay: 0.5, z: 4 });
    parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 32, 34, 11, 10), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
    parts.push({ kind: 'head', tex: hoodTex(key, p, cloth), x: 0, y: -42, ax: 0.5, ay: 0.95, z: 3 });
    return { parts, height: Math.round(62 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
  }
  parts.push({ kind: 'weapon', tex: staffTex(key, p), x: 12, y: -18, ax: 0.5, ay: 0.85, z: 4 });
  parts.push({ kind: 'body', tex: cloakTex(key, p, cloth, 34, 36, 11, 14), x: 0, y: -12, ax: 0.5, ay: 1, z: 2 });
  parts.push({ kind: 'head', tex: hatTex(key, p, cloth), x: 0, y: -44, ax: 0.5, ay: 0.95, z: 3 });
  return { parts, height: Math.round(70 * HERO_SCALE), family: 'walker', scale: HERO_SCALE };
}

// ------------------------------------------------------------------ monsters

function ghoulBodyTex(key: string, p: Palette, w: number, h: number, kind: 'ghoul' | 'skeleton' | 'robed' | 'bloated'): Texture {
  return paint('mobbody:' + key, w, h, (ctx) => {
    const cx = w / 2;
    if (kind === 'bloated') {
      ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
      ctx.beginPath();
      ctx.ellipse(cx, h * 0.62, w * 0.42, h * 0.36, 0, 0, Math.PI * 2);
      ctx.fill();
      rim(ctx, p.accent, 1.4, 0.5);
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.beginPath();
      ctx.ellipse(cx - w * 0.1, h * 0.5, w * 0.16, h * 0.12, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (kind === 'robed') {
      cloakPath(ctx, w, h * 0.3, h - 1, w * 0.3, w * 0.42, 3);
      ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
      ctx.fill();
      rim(ctx, p.accent, 1.3, 0.5);
    } else {
      // hunched torso with arms
      ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
      ctx.beginPath();
      ctx.moveTo(cx - w * 0.28, h * 0.3);
      ctx.quadraticCurveTo(cx, h * 0.1, cx + w * 0.3, h * 0.3);
      ctx.quadraticCurveTo(cx + w * 0.38, h * 0.6, cx + w * 0.22, h * 0.98);
      ctx.lineTo(cx - w * 0.22, h * 0.98);
      ctx.quadraticCurveTo(cx - w * 0.38, h * 0.6, cx - w * 0.28, h * 0.3);
      ctx.closePath();
      ctx.fill();
      rim(ctx, p.accent, 1.3, 0.45);
      // arms hanging
      ctx.strokeStyle = p.dark;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(cx + s * w * 0.3, h * 0.38);
        ctx.quadraticCurveTo(cx + s * w * 0.46, h * 0.6, cx + s * w * 0.4, h * 0.85);
        ctx.stroke();
      }
      if (kind === 'skeleton') {
        ctx.strokeStyle = 'rgba(232,224,204,0.75)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(cx - w * 0.16, h * 0.4 + i * 3.2);
          ctx.lineTo(cx + w * 0.16, h * 0.4 + i * 3.2);
          ctx.stroke();
        }
      }
    }
  });
}

function mobHeadTex(key: string, p: Palette, w: number, h: number, kind: 'skull' | 'hood' | 'blob' | 'crown'): Texture {
  return paint('mobhead:' + key, w, h, (ctx) => {
    const cx = w / 2;
    if (kind === 'crown') {
      ctx.fillStyle = '#c9a86a';
      ctx.beginPath();
      ctx.moveTo(cx - w * 0.35, h * 0.35);
      for (let i = 0; i < 5; i++) ctx.lineTo(cx - w * 0.35 + (w * 0.7 * i) / 4, i % 2 === 0 ? h * 0.05 : h * 0.3);
      ctx.lineTo(cx + w * 0.35, h * 0.35);
      ctx.closePath();
      ctx.fill();
    }
    ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
    ctx.beginPath();
    if (kind === 'hood') {
      ctx.moveTo(cx - w * 0.4, h);
      ctx.quadraticCurveTo(cx - w * 0.42, h * 0.2, cx, h * 0.02);
      ctx.quadraticCurveTo(cx + w * 0.42, h * 0.2, cx + w * 0.4, h);
      ctx.closePath();
    } else {
      ctx.ellipse(cx, h * 0.55, w * 0.38, h * 0.42, 0, 0, Math.PI * 2);
    }
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.45);
    if (kind === 'hood') {
      ctx.fillStyle = '#07060b';
      ctx.beginPath();
      ctx.ellipse(cx, h * 0.62, w * 0.22, h * 0.24, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    eye(ctx, cx - w * 0.13, h * 0.58, w * 0.07, p.accent);
    eye(ctx, cx + w * 0.13, h * 0.58, w * 0.07, p.accent);
    if (kind === 'skull') {
      ctx.fillStyle = '#05040a';
      for (let i = -1; i <= 1; i++) ctx.fillRect(cx + i * 2.4 - 0.8, h * 0.78, 1.6, 3);
    }
  });
}

function beastTex(key: string, p: Palette, w: number, h: number, ears: boolean): Texture {
  return paint('beast:' + key, w, h, (ctx) => {
    ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
    ctx.beginPath();
    ctx.ellipse(w * 0.45, h * 0.55, w * 0.34, h * 0.26, 0, 0, Math.PI * 2);
    ctx.fill();
    // head
    ctx.beginPath();
    ctx.ellipse(w * 0.8, h * 0.42, w * 0.17, h * 0.2, 0, 0, Math.PI * 2);
    ctx.fill();
    // snout
    ctx.beginPath();
    ctx.moveTo(w * 0.9, h * 0.38);
    ctx.lineTo(w * 1.02, h * 0.48);
    ctx.lineTo(w * 0.9, h * 0.54);
    ctx.closePath();
    ctx.fill();
    if (ears) {
      ctx.beginPath();
      ctx.moveTo(w * 0.72, h * 0.28);
      ctx.lineTo(w * 0.7, h * 0.08);
      ctx.lineTo(w * 0.8, h * 0.26);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(w * 0.84, h * 0.26);
      ctx.lineTo(w * 0.88, h * 0.06);
      ctx.lineTo(w * 0.94, h * 0.28);
      ctx.closePath();
      ctx.fill();
    }
    // tail
    ctx.strokeStyle = p.base;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(w * 0.14, h * 0.5);
    ctx.quadraticCurveTo(w * 0.0, h * 0.3, w * 0.06, h * 0.12);
    ctx.stroke();
    // legs
    ctx.fillStyle = p.dark;
    for (const lx of [0.28, 0.4, 0.58, 0.68]) ctx.fillRect(w * lx - 2, h * 0.7, 4, h * 0.3);
    ctx.beginPath();
    ctx.ellipse(w * 0.45, h * 0.55, w * 0.34, h * 0.26, 0, 0, Math.PI * 2);
    rim(ctx, p.accent, 1.2, 0.45);
    eye(ctx, w * 0.84, h * 0.4, 1.4, p.accent);
  });
}

function blobTex(key: string, p: Palette, cw: number, h: number, eyes: number, bodyW = cw): Texture {
  return paint('blob:' + key, cw, h, (ctx) => {
    const cx = cw / 2;
    const w = bodyW;
    if (eyes >= 3) {
      // tendrils for the big horrors, reaching outside the body
      ctx.strokeStyle = p.dark;
      ctx.lineWidth = Math.max(2, w * 0.05);
      ctx.lineCap = 'round';
      for (let i = 0; i < 7; i++) {
        const a = -Math.PI * 0.1 + (i / 6) * Math.PI * 1.2;
        const dir = Math.cos(a) < 0 ? -1 : 1;
        const sx = cx + Math.cos(a) * w * 0.3;
        const sy = h * 0.6 + Math.abs(Math.sin(a)) * h * 0.15;
        const ex = cx + dir * (w * 0.5 + (cw - w) * 0.45) * (0.7 + Math.abs(Math.cos(a)) * 0.3);
        const ey = h * 0.92 - (i % 3) * h * 0.22;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.quadraticCurveTo(cx + dir * w * 0.62, sy - h * 0.18, ex, ey);
        ctx.stroke();
        glow(ctx, p.accent, 4);
        ctx.fillStyle = p.accent;
        ctx.beginPath();
        ctx.arc(ex, ey, w * 0.02 + 1, 0, Math.PI * 2);
        ctx.fill();
        noGlow(ctx);
      }
    }
    const g = ctx.createRadialGradient(cx - w * 0.15, h * 0.35, 2, cx, h * 0.55, w * 0.5);
    g.addColorStop(0, p.light);
    g.addColorStop(0.7, p.base);
    g.addColorStop(1, p.dark);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(cx - w * 0.46, h * 0.85);
    ctx.quadraticCurveTo(cx - w * 0.5, h * 0.1, cx, h * 0.06);
    ctx.quadraticCurveTo(cx + w * 0.5, h * 0.1, cx + w * 0.46, h * 0.85);
    ctx.quadraticCurveTo(cx, h * 1.02, cx - w * 0.46, h * 0.85);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.3, 0.5);
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.beginPath();
    ctx.ellipse(cx - w * 0.16, h * 0.32, w * 0.12, h * 0.08, -0.5, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < eyes; i++) {
      const a = (i / eyes) * Math.PI * 2;
      eye(ctx, cx + Math.cos(a) * w * 0.18, h * 0.55 + Math.sin(a) * h * 0.14, eyes > 2 ? 1.3 : 2, p.accent);
    }
  });
}

function wingTex(key: string, p: Palette, w: number, h: number): Texture {
  return paint('wing:' + key, w, h, (ctx) => {
    ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
    ctx.beginPath();
    ctx.moveTo(0, h * 0.5);
    ctx.quadraticCurveTo(w * 0.3, 0, w, h * 0.1);
    ctx.lineTo(w * 0.85, h * 0.45);
    ctx.lineTo(w * 0.95, h * 0.8);
    ctx.lineTo(w * 0.6, h * 0.6);
    ctx.lineTo(w * 0.5, h);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1, 0.4);
  });
}

function sporeTex(key: string, p: Palette, w: number, h: number): Texture {
  return paint('spore:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = vgrad(ctx, 0, h * 0.4, h, shadeHex(p.base, 1.1), p.dark);
    ctx.beginPath();
    ctx.moveTo(cx - w * 0.16, h * 0.45);
    ctx.lineTo(cx + w * 0.16, h * 0.45);
    ctx.lineTo(cx + w * 0.22, h);
    ctx.lineTo(cx - w * 0.22, h);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = vgrad(ctx, 0, 0, h * 0.5, p.light, p.base);
    ctx.beginPath();
    ctx.ellipse(cx, h * 0.34, w * 0.48, h * 0.3, 0, Math.PI, 0);
    ctx.quadraticCurveTo(cx, h * 0.5, cx - w * 0.48, h * 0.34);
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.45);
    glow(ctx, p.accent, 5);
    ctx.fillStyle = p.accent;
    for (const [dx, dy, r] of [
      [-0.22, 0.24, 2],
      [0.1, 0.14, 2.6],
      [0.3, 0.3, 1.6],
    ])
      ctx.beginPath(), ctx.arc(cx + w * dx, h * dy, r, 0, Math.PI * 2), ctx.fill();
    noGlow(ctx);
  });
}

function spiritTex(key: string, p: Palette, w: number, h: number): Texture {
  return paint('spirit:' + key, w, h, (ctx) => {
    const cx = w / 2;
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, p.light);
    g.addColorStop(0.55, p.base);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(cx - w * 0.36, h * 0.95);
    ctx.quadraticCurveTo(cx - w * 0.44, h * 0.3, cx, h * 0.04);
    ctx.quadraticCurveTo(cx + w * 0.44, h * 0.3, cx + w * 0.36, h * 0.95);
    for (let i = 0; i < 4; i++) ctx.lineTo(cx + w * 0.36 - (w * 0.72 * (i + 1)) / 4, i % 2 === 0 ? h * 0.82 : h * 0.95);
    ctx.closePath();
    ctx.fill();
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = p.accent;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(cx - w * 0.36, h * 0.6);
    ctx.quadraticCurveTo(cx - w * 0.44, h * 0.3, cx, h * 0.04);
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = '#07060b';
    ctx.beginPath();
    ctx.ellipse(cx, h * 0.34, w * 0.2, h * 0.16, 0, 0, Math.PI * 2);
    ctx.fill();
    eye(ctx, cx - w * 0.09, h * 0.33, w * 0.06, p.accent);
    eye(ctx, cx + w * 0.09, h * 0.33, w * 0.06, p.accent);
  });
}

function totemTex(key: string, p: Palette, w: number, h: number): Texture {
  return paint('totem:' + key, w, h, (ctx) => {
    const cx = w / 2;
    ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
    ctx.beginPath();
    ctx.moveTo(cx - w * 0.28, 4);
    ctx.lineTo(cx + w * 0.28, 4);
    ctx.lineTo(cx + w * 0.34, h);
    ctx.lineTo(cx - w * 0.34, h);
    ctx.closePath();
    ctx.fill();
    rim(ctx, p.accent, 1.2, 0.4);
    glow(ctx, p.accent, 5);
    ctx.fillStyle = p.accent;
    for (let i = 0; i < 3; i++) ctx.fillRect(cx - 3, 8 + i * (h * 0.24), 6, 2);
    ctx.beginPath();
    ctx.arc(cx, 4, 3, 0, Math.PI * 2);
    ctx.fill();
    noGlow(ctx);
  });
}

function knightBodyTex(key: string, p: Palette, w: number, h: number): Texture {
  return paint('knightbody:' + key, w, h, (ctx) => {
    const cx = w / 2;
    // pauldrons
    ctx.fillStyle = shadeHex(p.base, 1.6);
    ctx.beginPath();
    ctx.ellipse(cx - w * 0.3, h * 0.2, w * 0.18, h * 0.1, 0, 0, Math.PI * 2);
    ctx.ellipse(cx + w * 0.3, h * 0.2, w * 0.18, h * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    cloakPath(ctx, w, h * 0.16, h - 1, w * 0.34, w * 0.3, 3);
    ctx.fillStyle = vgrad(ctx, 0, 0, h, p.light, p.dark);
    ctx.fill();
    rim(ctx, p.accent, 1.4, 0.5);
    ctx.fillStyle = shadeHex(p.base, 1.8);
    ctx.fillRect(cx - w * 0.14, h * 0.25, w * 0.28, h * 0.32);
    ctx.fillStyle = p.accent;
    ctx.globalAlpha = 0.7;
    ctx.fillRect(cx - 1, h * 0.28, 2, h * 0.26);
    ctx.globalAlpha = 1;
  });
}

function bigWeaponTex(key: string, p: Palette, kind: 'sword' | 'anchor' | 'club', w = 16, h = 52): Texture {
  return paint('bigweapon:' + key, w, h, (ctx) => {
    const cx = w / 2;
    if (kind === 'sword') {
      glow(ctx, p.accent, 5);
      ctx.fillStyle = vgrad(ctx, 0, 0, h * 0.7, '#f0e0c0', '#8a6a4a');
      ctx.beginPath();
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx + 4.5, 6);
      ctx.lineTo(cx + 3.5, h * 0.66);
      ctx.lineTo(cx - 3.5, h * 0.66);
      ctx.lineTo(cx - 4.5, 6);
      ctx.closePath();
      ctx.fill();
      noGlow(ctx);
      ctx.fillStyle = p.accent;
      ctx.fillRect(cx - 7, h * 0.66, 14, 3);
      ctx.fillStyle = '#2a2230';
      ctx.fillRect(cx - 1.8, h * 0.7, 3.6, h * 0.28);
    } else if (kind === 'anchor') {
      ctx.strokeStyle = '#6a7a88';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx, 3);
      ctx.lineTo(cx, h * 0.72);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, h * 0.62, 7, 0.1, Math.PI - 0.1);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, 5, 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#6a7a88';
      ctx.fillRect(cx - 6, 12, 12, 2.4);
    } else {
      ctx.fillStyle = vgrad(ctx, 0, 0, h, '#6a5a4a', '#2a2018');
      ctx.beginPath();
      ctx.moveTo(cx - 2, h);
      ctx.lineTo(cx + 2, h);
      ctx.lineTo(cx + 6, 8);
      ctx.lineTo(cx, 0);
      ctx.lineTo(cx - 6, 8);
      ctx.closePath();
      ctx.fill();
    }
  });
}

interface MobStyleSpec {
  family: UnitArt['family'];
  bodyKind?: 'ghoul' | 'skeleton' | 'robed' | 'bloated';
  headKind?: 'skull' | 'hood' | 'blob' | 'crown';
  weapon?: 'sword' | 'anchor' | 'club' | 'bow';
  eyes?: number;
  ears?: boolean;
}

const MOB_STYLES: Record<string, MobStyleSpec> = {
  ghoul: { family: 'walker', bodyKind: 'ghoul', headKind: 'skull' },
  skeleton_archer: { family: 'walker', bodyKind: 'skeleton', headKind: 'skull', weapon: 'bow' },
  spore: { family: 'static' },
  hound: { family: 'beast', ears: true },
  bat_swarm: { family: 'flyer' },
  cultist: { family: 'walker', bodyKind: 'robed', headKind: 'hood' },
  wraith: { family: 'spirit' },
  bone_knight: { family: 'walker', bodyKind: 'skeleton', headKind: 'skull', weapon: 'sword' },
  slime: { family: 'blob', eyes: 2 },
  slimelet: { family: 'blob', eyes: 1 },
  drowned: { family: 'walker', bodyKind: 'bloated', headKind: 'blob' },
  ash_guard: { family: 'walker', bodyKind: 'ghoul', headKind: 'skull', weapon: 'sword' },
  rot_priest: { family: 'walker', bodyKind: 'robed', headKind: 'hood' },
  storm_wraith: { family: 'spirit' },
  ash_knight: { family: 'walker', weapon: 'sword' },
  rot_mother: { family: 'blob', eyes: 5 },
  drowned_king: { family: 'walker', bodyKind: 'bloated', headKind: 'crown', weapon: 'anchor' },
  shade: { family: 'spirit' },
  shade_lesser: { family: 'spirit' },
  shade_champion: { family: 'walker', weapon: 'sword' },
  totem: { family: 'static' },
  healing_totem: { family: 'static' },
  thunder_totem: { family: 'static' },
  spore_turret: { family: 'static' },
  void_maw: { family: 'blob', eyes: 3 },
  skeleton: { family: 'walker', bodyKind: 'skeleton', headKind: 'skull' },
  imp: { family: 'spirit' },
  imp_lesser: { family: 'spirit' },
  fiend: { family: 'walker', bodyKind: 'ghoul', headKind: 'skull' },
  thornroot: { family: 'static' },
  bloomroot: { family: 'static' },
  kindred_bear: { family: 'beast', ears: true },
  kindred_wolf: { family: 'beast', ears: true },
  kindred_ancient: { family: 'beast', ears: true },
  gob_turret: { family: 'static' },
  gob_cannon: { family: 'static' },
  pet_wolf: { family: 'beast', ears: true },
  pet_raven: { family: 'flyer' },
  pet_wisp: { family: 'spirit' },
  pet_tortoise: { family: 'blob', eyes: 2 },
  pet_bat: { family: 'flyer' },
  pet_cat: { family: 'beast', ears: true },
};

export function buildMob(defId: string, elite = false): UnitArt {
  const def = MOBS[defId];
  const style = MOB_STYLES[defId] ?? { family: 'walker', bodyKind: 'ghoul', headKind: 'skull' };
  const size = (def?.size ?? 26) * (elite && !def?.boss ? 1.25 : 1);
  const base = def?.color ?? '#555555';
  const accent = def?.accent ?? '#ffffff';
  const p = palette(elite ? shadeHex(base, 1.2) : base, accent);
  const key = defId + (elite ? ':e' : '');
  const parts: PartSpec[] = [];
  const isBoss = !!def?.boss;
  switch (style.family) {
    case 'walker': {
      const h = size;
      const bw = Math.round(h * (isBoss ? 0.72 : 0.58));
      const bh = Math.round(h * 0.58);
      const legH = Math.round(h * 0.32);
      const leg = legTex(key, p, Math.max(5, Math.round(bw * 0.22)), legH);
      parts.push({ kind: 'legL', tex: leg, x: -bw * 0.16, y: -legH, ax: 0.5, ay: 0.05, z: 1 });
      parts.push({ kind: 'legR', tex: leg, x: bw * 0.16, y: -legH, ax: 0.5, ay: 0.05, z: 1 });
      const body = isBoss || style.weapon === 'sword' && !style.bodyKind ? knightBodyTex(key, p, bw, bh) : ghoulBodyTex(key, p, bw, bh, style.bodyKind ?? 'ghoul');
      parts.push({ kind: 'body', tex: body, x: 0, y: -legH * 0.85, ax: 0.5, ay: 1, z: 2 });
      const hw = Math.round(bw * 0.5);
      const hh = Math.round(bw * 0.46);
      if (isBoss && !style.headKind) parts.push({ kind: 'head', tex: helmTex(key, p, true, hw, hh), x: 0, y: -legH * 0.85 - bh + 4, ax: 0.5, ay: 0.95, z: 3 });
      else parts.push({ kind: 'head', tex: mobHeadTex(key, p, hw, hh, style.headKind ?? 'skull'), x: 0, y: -legH * 0.85 - bh + 4, ax: 0.5, ay: 0.95, z: 3 });
      if (style.weapon === 'bow') parts.push({ kind: 'weapon', tex: bowTex(key, p, 10, Math.round(h * 0.7)), x: bw * 0.36, y: -h * 0.5, ax: 0.35, ay: 0.5, z: 4 });
      else if (style.weapon) parts.push({ kind: 'weapon', tex: bigWeaponTex(key, p, style.weapon, 16, Math.round(h * 0.9)), x: bw * 0.36, y: -h * 0.45, ax: 0.5, ay: 0.72, z: 4 });
      return { parts, height: h + hh * 0.6, family: 'walker' };
    }
    case 'beast': {
      const w = size * 1.3;
      const h = size * 0.75;
      parts.push({ kind: 'body', tex: beastTex(key, p, Math.round(w), Math.round(h), !!style.ears), x: 0, y: 0, ax: 0.5, ay: 1, z: 2 });
      return { parts, height: h, family: 'beast' };
    }
    case 'blob': {
      const w = size * 1.15;
      const h = size * 0.9;
      const eyes = style.eyes ?? 2;
      const cw = eyes >= 3 ? w * 1.6 : w;
      parts.push({ kind: 'body', tex: blobTex(key, p, Math.round(cw), Math.round(h), eyes, Math.round(w)), x: 0, y: 0, ax: 0.5, ay: 1, z: 2 });
      return { parts, height: h, family: 'blob' };
    }
    case 'flyer': {
      const bw = size * 0.6;
      const bh = size * 0.5;
      const wing = wingTex(key, p, Math.round(size * 0.7), Math.round(size * 0.5));
      parts.push({ kind: 'wingL', tex: wing, x: -bw * 0.3, y: -bh * 0.9, ax: 1, ay: 0.5, z: 1 });
      parts.push({ kind: 'wingR', tex: wing, x: bw * 0.3, y: -bh * 0.9, ax: 1, ay: 0.5, z: 1 });
      parts.push({ kind: 'body', tex: blobTex(key + ':b', p, Math.round(bw), Math.round(bh), 2), x: 0, y: -bh * 0.4, ax: 0.5, ay: 1, z: 2 });
      return { parts, height: size, family: 'flyer' };
    }
    case 'spirit': {
      const w = size * 0.8;
      const h = size * 1.1;
      parts.push({ kind: 'body', tex: spiritTex(key, p, Math.round(w), Math.round(h)), x: 0, y: 2, ax: 0.5, ay: 1, z: 2, alpha: 0.9 });
      return { parts, height: h, family: 'spirit' };
    }
    case 'static':
    default: {
      const w = size * 0.9;
      const h = size;
      const tex = defId.includes('totem') || defId.startsWith('gob_') ? totemTex(key, p, Math.round(w * 0.7), Math.round(h)) : sporeTex(key, p, Math.round(w), Math.round(h));
      parts.push({ kind: 'body', tex, x: 0, y: 0, ax: 0.5, ay: 1, z: 2 });
      return { parts, height: h, family: 'static' };
    }
  }
}

export function petArt(petId: string): UnitArt | null {
  const def = PETS[petId];
  return def ? buildMob(def.unit) : null;
}

/** Composes an art's parts onto a square canvas for menus, fitted to the canvas size. */
export function artDataUrl(art: UnitArt, size = 128): string {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  const parts = [...art.parts].sort((a, b) => a.z - b.z);
  // bounds in logical units relative to the feet point
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of parts) {
    const src = p.tex.source.resource as HTMLCanvasElement;
    if (!src) continue;
    const pw = src.width / RES;
    const ph = src.height / RES;
    minX = Math.min(minX, p.x - pw * p.ax);
    maxX = Math.max(maxX, p.x + pw * (1 - p.ax));
    minY = Math.min(minY, p.y - ph * p.ay);
    maxY = Math.max(maxY, p.y + ph * (1 - p.ay));
  }
  if (!isFinite(minX)) return c.toDataURL();
  const bw = maxX - minX;
  const bh = maxY - minY;
  const scale = (size * 0.86) / Math.max(bw, bh);
  const ox = size / 2 - ((minX + maxX) / 2) * scale;
  const oy = size / 2 - ((minY + maxY) / 2) * scale;
  for (const p of parts) {
    const src = p.tex.source.resource as HTMLCanvasElement;
    if (!src) continue;
    const pw = src.width / RES;
    const ph = src.height / RES;
    ctx.globalAlpha = p.alpha ?? 1;
    ctx.drawImage(src, ox + (p.x - pw * p.ax) * scale, oy + (p.y - ph * p.ay) * scale, pw * scale, ph * scale);
  }
  return c.toDataURL();
}

// ------------------------------------------------------------------ environment

interface TierPalette {
  ground: string;
  groundLight: string;
  groundDark: string;
  detail: string;
  fog: number;
  grade: number;
  particle: number;
  rock: string;
}

export const TIER_PALETTES: Record<number, TierPalette> = {
  1: { ground: '#18241f', groundLight: '#243530', groundDark: '#0e1713', detail: '#3f7a5a', fog: 0x2a6a58, grade: 0x1a4a3a, particle: 0x9ff0c0, rock: '#2d3a36' },
  2: { ground: '#221e2a', groundLight: '#322c3d', groundDark: '#141019', detail: '#7a6f80', fog: 0x5a4a7a, grade: 0x3a2a5a, particle: 0xb0a8c0, rock: '#3b3346' },
  3: { ground: '#261412', groundLight: '#3a1f1a', groundDark: '#150a08', detail: '#ff7a2f', fog: 0x6a2a20, grade: 0x5a1a10, particle: 0xffa040, rock: '#3d2320' },
};

/** Cheap value noise for painterly textures. */
function valueNoise(seed: number) {
  const rand = (x: number, y: number) => {
    let n = (x * 374761393 + y * 668265263 + seed * 1274126177) | 0;
    n = (n ^ (n >>> 13)) * 1274126177;
    return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
  };
  const smooth = (t: number) => t * t * (3 - 2 * t);
  return (x: number, y: number): number => {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = smooth(x - x0);
    const fy = smooth(y - y0);
    const a = rand(x0, y0);
    const b = rand(x0 + 1, y0);
    const c = rand(x0, y0 + 1);
    const d = rand(x0 + 1, y0 + 1);
    return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
  };
}

const SHEET = 256;

/** 256x256 painterly ground sheet per tier; tiles are sub-frames. */
export function groundSheet(tier: number): TextureSource {
  const key = 'ground3:' + tier;
  const hit = cache.get(key);
  if (hit) return hit.source;
  const pal = TIER_PALETTES[tier] ?? TIER_PALETTES[1];
  const c = document.createElement('canvas');
  c.width = SHEET;
  c.height = SHEET;
  const ctx = c.getContext('2d')!;
  const n1 = valueNoise(tier * 11 + 1);
  const n2 = valueNoise(tier * 11 + 2);
  const n3 = valueNoise(tier * 11 + 3);
  const img = ctx.createImageData(SHEET, SHEET);
  const base = parseInt(pal.ground.slice(1), 16);
  const br = (base >> 16) & 255;
  const bg = (base >> 8) & 255;
  const bb = base & 255;
  for (let y = 0; y < SHEET; y++) {
    for (let x = 0; x < SHEET; x++) {
      const v = n1(x / 42, y / 42) * 0.55 + n2(x / 14, y / 14) * 0.3 + n3(x / 5, y / 5) * 0.15;
      const f = 0.72 + v * 0.6;
      const i = (y * SHEET + x) * 4;
      img.data[i] = Math.min(255, br * f);
      img.data[i + 1] = Math.min(255, bg * f);
      img.data[i + 2] = Math.min(255, bb * f);
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  // painterly patches
  let seed = tier * 977;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  for (let i = 0; i < 70; i++) {
    ctx.fillStyle = rnd() < 0.5 ? pal.groundDark : pal.groundLight;
    ctx.globalAlpha = 0.12 + rnd() * 0.12;
    ctx.beginPath();
    ctx.ellipse(rnd() * SHEET, rnd() * SHEET, 6 + rnd() * 30, 4 + rnd() * 18, rnd() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  // cracks
  ctx.strokeStyle = pal.groundDark;
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 26; i++) {
    let x = rnd() * SHEET;
    let y = rnd() * SHEET;
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let s = 0; s < 8; s++) {
      x += (rnd() - 0.5) * 14;
      y += (rnd() - 0.3) * 12;
      ctx.lineTo(x, y);
    }
    ctx.globalAlpha = 0.35 + rnd() * 0.3;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  // tier details
  if (tier === 1) {
    ctx.strokeStyle = pal.detail;
    ctx.lineWidth = 1;
    for (let i = 0; i < 160; i++) {
      const x = rnd() * SHEET;
      const y = rnd() * SHEET;
      ctx.globalAlpha = 0.35 + rnd() * 0.4;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + (rnd() - 0.5) * 4, y - 3, x + (rnd() - 0.5) * 6, y - 5 - rnd() * 4);
      ctx.stroke();
    }
  } else if (tier === 2) {
    ctx.strokeStyle = '#c8bca0';
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 40; i++) {
      const x = rnd() * SHEET;
      const y = rnd() * SHEET;
      const a = rnd() * Math.PI;
      ctx.globalAlpha = 0.25 + rnd() * 0.3;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * 5, y + Math.sin(a) * 5);
      ctx.stroke();
    }
  } else {
    for (let i = 0; i < 60; i++) {
      const x = rnd() * SHEET;
      const y = rnd() * SHEET;
      ctx.globalAlpha = 0.5 + rnd() * 0.5;
      ctx.shadowColor = pal.detail;
      ctx.shadowBlur = 6;
      ctx.fillStyle = rnd() < 0.5 ? pal.detail : '#ffb060';
      ctx.fillRect(x, y, 1.5, 1.5);
    }
    ctx.shadowBlur = 0;
  }
  ctx.globalAlpha = 1;
  const tex = Texture.from(c);
  tex.source.scaleMode = 'linear';
  cache.set(key, tex);
  return tex.source;
}

const frameCache = new Map<string, Texture>();
export function groundFrame(tier: number, fx: number, fy: number): Texture {
  const key = `gf:${tier}:${fx}:${fy}`;
  const hit = frameCache.get(key);
  if (hit) return hit;
  const src = groundSheet(tier);
  const t = new Texture({ source: src, frame: new Rectangle(fx * 32, fy * 32, 32, 32) });
  frameCache.set(key, t);
  return t;
}

/** Rock wall tile; mask bits: 1 N open, 2 E open, 4 S open, 8 W open. */
export function wallTile(tier: number, mask: number, variant: number): Texture {
  const key = `wall3:${tier}:${mask}:${variant}`;
  const pal = TIER_PALETTES[tier] ?? TIER_PALETTES[1];
  return paint(key, 32, 32, (ctx) => {
    const base = pal.rock;
    ctx.fillStyle = vgrad(ctx, 0, 0, 32, shadeHex(base, 1.15), shadeHex(base, 0.7));
    ctx.fillRect(0, 0, 32, 32);
    let seed = (tier * 31 + variant * 7 + mask) >>> 0;
    const rnd = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    };
    // rock facets
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = shadeHex(base, 0.8 + rnd() * 0.5);
      ctx.beginPath();
      const x = rnd() * 32;
      const y = rnd() * 32;
      ctx.moveTo(x, y);
      ctx.lineTo(x + 4 + rnd() * 10, y + (rnd() - 0.5) * 6);
      ctx.lineTo(x + 2 + rnd() * 8, y + 4 + rnd() * 8);
      ctx.lineTo(x - 2 + rnd() * 4, y + 3 + rnd() * 6);
      ctx.closePath();
      ctx.fill();
    }
    // cracks
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      let x = rnd() * 32;
      let y = rnd() * 32;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let s = 0; s < 4; s++) {
        x += (rnd() - 0.5) * 8;
        y += rnd() * 6;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    // open edges: light on top, dark on the sides/bottom
    if (mask & 1) {
      ctx.fillStyle = vgrad(ctx, 0, 0, 6, shadeHex(base, 1.9), 'rgba(0,0,0,0)');
      ctx.fillRect(0, 0, 32, 6);
    }
    if (mask & 4) {
      ctx.fillStyle = vgrad(ctx, 0, 26, 32, 'rgba(0,0,0,0)', 'rgba(0,0,0,0.6)');
      ctx.fillRect(0, 26, 32, 6);
    }
    if (mask & 8) {
      const g = ctx.createLinearGradient(0, 0, 5, 0);
      g.addColorStop(0, shadeHex(base, 1.5));
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 5, 32);
    }
    if (mask & 2) {
      const g = ctx.createLinearGradient(27, 0, 32, 0);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, 'rgba(0,0,0,0.5)');
      ctx.fillStyle = g;
      ctx.fillRect(27, 0, 5, 32);
    }
    if (tier === 1 && rnd() < 0.5) {
      ctx.fillStyle = 'rgba(60,110,70,0.5)';
      for (let i = 0; i < 4; i++) ctx.fillRect(rnd() * 30, rnd() * 30, 2 + rnd() * 3, 1.5);
    }
  });
}

/** Soft cloud for drifting fog. */
export function fogTexture(): Texture {
  return paint('fog', 256, 256, (ctx) => {
    const n = valueNoise(99);
    const img = ctx.createImageData(256 * RES, 256 * RES);
    const S = 256 * RES;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const dx = x / S - 0.5;
        const dy = y / S - 0.5;
        const d = Math.sqrt(dx * dx + dy * dy) * 2;
        const v = n(x / 60, y / 60) * 0.6 + n(x / 20, y / 20) * 0.4;
        const a = Math.max(0, 1 - d) * (0.35 + v * 0.65);
        const i = (y * S + x) * 4;
        img.data[i] = 255;
        img.data[i + 1] = 255;
        img.data[i + 2] = 255;
        img.data[i + 3] = Math.round(a * 255);
      }
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.putImageData(img, 0, 0);
  });
}

// ------------------------------------------------------------------ props

export function propTex(kind: string, tier: number, variant = 0): Texture {
  const pal = TIER_PALETTES[tier] ?? TIER_PALETTES[1];
  const key = `prop3:${kind}:${tier}:${variant}`;
  switch (kind) {
    case 'dead_tree':
      return paint(key, 48, 64, (ctx, w, h) => {
        let seed = variant * 7 + tier;
        const rnd = () => {
          seed = (seed * 1664525 + 1013904223) >>> 0;
          return seed / 4294967296;
        };
        const branch = (x: number, y: number, a: number, len: number, wdt: number, depth: number) => {
          const x2 = x + Math.cos(a) * len;
          const y2 = y + Math.sin(a) * len;
          ctx.strokeStyle = vgrad(ctx, 0, y2, y, '#1a1216', '#3a2a2c');
          ctx.lineWidth = wdt;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          if (depth <= 0 || wdt < 0.8) return;
          const n = 2 + (rnd() < 0.4 ? 1 : 0);
          for (let i = 0; i < n; i++) branch(x2, y2, a + (rnd() - 0.5) * 1.6, len * (0.55 + rnd() * 0.25), wdt * 0.62, depth - 1);
        };
        branch(w / 2, h, -Math.PI / 2 + (rnd() - 0.5) * 0.3, h * 0.36, 5, 4);
        // roots
        ctx.strokeStyle = '#1a1216';
        ctx.lineWidth = 2;
        for (const s of [-1, 1]) {
          ctx.beginPath();
          ctx.moveTo(w / 2, h - 2);
          ctx.quadraticCurveTo(w / 2 + s * 6, h - 1, w / 2 + s * 12, h);
          ctx.stroke();
        }
      });
    case 'tombstone':
      return paint(key, 24, 30, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(pal.rock, 1.6), shadeHex(pal.rock, 0.8));
        ctx.beginPath();
        ctx.moveTo(3, h);
        ctx.lineTo(3, 10);
        ctx.quadraticCurveTo(w / 2, -4, w - 3, 10);
        ctx.lineTo(w - 3, h);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.beginPath();
        ctx.moveTo(8, 14);
        ctx.lineTo(12, 22);
        ctx.stroke();
        if (variant % 2 === 0) {
          glow(ctx, pal.detail, 5);
          ctx.fillStyle = pal.detail;
          ctx.globalAlpha = 0.8;
          ctx.fillRect(w / 2 - 1, 12, 2, 8);
          ctx.fillRect(w / 2 - 4, 15, 8, 2);
          noGlow(ctx);
          ctx.globalAlpha = 1;
        }
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(1, h - 3, w - 2, 3);
      });
    case 'cross':
      return paint(key, 22, 34, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, '#5a4a3a', '#2a2018');
        ctx.fillRect(w / 2 - 2.5, 2, 5, h - 2);
        ctx.fillRect(3, 10, w - 6, 4.5);
        ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.lineWidth = 1;
        ctx.strokeRect(w / 2 - 2.5, 2, 5, h - 2);
      });
    case 'skull':
      return paint(key, 16, 14, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, '#e6dcc4', '#9a8f78');
        ctx.beginPath();
        ctx.ellipse(w / 2, h * 0.45, w * 0.42, h * 0.42, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(w * 0.3, h * 0.6, w * 0.4, h * 0.35);
        ctx.fillStyle = '#0b0a10';
        ctx.beginPath();
        ctx.ellipse(w * 0.36, h * 0.45, 2.2, 2.6, 0, 0, Math.PI * 2);
        ctx.ellipse(w * 0.64, h * 0.45, 2.2, 2.6, 0, 0, Math.PI * 2);
        ctx.fill();
        for (let i = 0; i < 3; i++) ctx.fillRect(w * 0.38 + i * 3, h * 0.8, 1, 3);
      });
    case 'bones':
      return paint(key, 24, 12, (ctx, w, h) => {
        ctx.strokeStyle = '#d8ccb0';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(3, h - 3);
        ctx.lineTo(w - 3, 3);
        ctx.moveTo(3, 4);
        ctx.lineTo(w - 4, h - 3);
        ctx.stroke();
        ctx.fillStyle = '#d8ccb0';
        for (const [x, y] of [
          [3, h - 3],
          [w - 3, 3],
          [3, 4],
          [w - 4, h - 3],
        ]) {
          ctx.beginPath();
          ctx.arc(x, y, 2.2, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    case 'rock':
      return paint(key, 28, 18, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(pal.rock, 1.7), shadeHex(pal.rock, 0.7));
        ctx.beginPath();
        ctx.moveTo(2, h - 2);
        ctx.lineTo(5, 7);
        ctx.lineTo(12, 2);
        ctx.lineTo(20, 4);
        ctx.lineTo(w - 2, 10);
        ctx.lineTo(w - 4, h - 2);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.2)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(5, 7);
        ctx.lineTo(12, 2);
        ctx.lineTo(20, 4);
        ctx.stroke();
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.fillRect(2, h - 3, w - 6, 3);
      });
    case 'bush':
      return paint(key, 28, 20, (ctx, w, h) => {
        for (let i = 0; i < 6; i++) {
          const x = 6 + (i * (w - 12)) / 5 + (i % 2) * 2;
          const y = h - 6 - (i % 3) * 3;
          ctx.fillStyle = i % 2 === 0 ? '#1c2a22' : '#243428';
          ctx.beginPath();
          ctx.arc(x, y, 6 + (i % 2), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.strokeStyle = 'rgba(120,180,130,0.25)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(w / 2, h - 8, 9, Math.PI, Math.PI * 1.8);
        ctx.stroke();
      });
    case 'ember_stone':
      return paint(key, 26, 18, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, '#3a2422', '#160a08');
        ctx.beginPath();
        ctx.moveTo(2, h - 2);
        ctx.lineTo(6, 5);
        ctx.lineTo(14, 2);
        ctx.lineTo(w - 3, 8);
        ctx.lineTo(w - 4, h - 2);
        ctx.closePath();
        ctx.fill();
        glow(ctx, '#ff7a2f', 6);
        ctx.strokeStyle = '#ff9a3c';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(8, h - 4);
        ctx.lineTo(11, 9);
        ctx.lineTo(16, 12);
        ctx.lineTo(19, 5);
        ctx.stroke();
        noGlow(ctx);
      });
    case 'mushrooms':
      return paint(key, 18, 14, (ctx, w, h) => {
        for (const [x, r, hh] of [
          [5, 4, 8],
          [12, 3, 6],
          [9, 2.5, 4],
        ]) {
          ctx.fillStyle = '#3a2a3a';
          ctx.fillRect(x - 1, h - hh, 2, hh);
          glow(ctx, '#9fe0c0', 5);
          ctx.fillStyle = '#6a4a6a';
          ctx.beginPath();
          ctx.ellipse(x, h - hh, r, r * 0.6, 0, Math.PI, 0);
          ctx.fill();
          noGlow(ctx);
          ctx.fillStyle = 'rgba(160,240,200,0.7)';
          ctx.beginPath();
          ctx.arc(x - r * 0.3, h - hh - 1, 0.8, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    case 'lantern':
      return paint(key, 12, 40, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, '#3a3040', '#1a141c');
        ctx.fillRect(w / 2 - 1.2, 8, 2.4, h - 8);
        ctx.fillRect(w / 2 - 4, 6, 8, 2);
        glow(ctx, '#ffb060', 8);
        ctx.fillStyle = '#ffd08a';
        ctx.fillRect(w / 2 - 2.5, 0, 5, 7);
        noGlow(ctx);
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(w / 2 - 4, h - 2, 8, 2);
      });
    case 'pillar':
      return paint(key, 20, 44, (ctx, w, h) => {
        ctx.fillStyle = vgrad(ctx, 0, 0, h, shadeHex(pal.rock, 1.5), shadeHex(pal.rock, 0.7));
        ctx.beginPath();
        ctx.moveTo(4, h);
        ctx.lineTo(5, 8);
        ctx.lineTo(9, 2);
        ctx.lineTo(14, 6);
        ctx.lineTo(16, h);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(5, 12 + i * 10);
          ctx.lineTo(15, 14 + i * 10);
          ctx.stroke();
        }
      });
    default:
      return paint(key, 12, 12, (ctx) => {
        ctx.fillStyle = pal.rock;
        ctx.fillRect(0, 0, 12, 12);
      });
  }
}

export interface DecorKind {
  kind: string;
  scale: number;
  tall: boolean;
  weight: number;
  light?: { color: number; radius: number };
}

export const DECOR_SETS: Record<number, DecorKind[]> = {
  1: [
    { kind: 'dead_tree', scale: 1.4, tall: true, weight: 4 },
    { kind: 'bush', scale: 1.2, tall: false, weight: 4 },
    { kind: 'tombstone', scale: 1.1, tall: true, weight: 2 },
    { kind: 'rock', scale: 1.1, tall: false, weight: 3 },
    { kind: 'mushrooms', scale: 1.2, tall: false, weight: 3, light: { color: 0x7fe0b0, radius: 60 } },
    { kind: 'cross', scale: 1, tall: true, weight: 1 },
    { kind: 'lantern', scale: 1.1, tall: true, weight: 1, light: { color: 0xffb060, radius: 150 } },
  ],
  2: [
    { kind: 'tombstone', scale: 1.2, tall: true, weight: 4 },
    { kind: 'skull', scale: 1.1, tall: false, weight: 3 },
    { kind: 'bones', scale: 1.1, tall: false, weight: 3 },
    { kind: 'dead_tree', scale: 1.5, tall: true, weight: 3 },
    { kind: 'rock', scale: 1.2, tall: false, weight: 2 },
    { kind: 'cross', scale: 1.1, tall: true, weight: 2 },
    { kind: 'pillar', scale: 1.1, tall: true, weight: 1 },
  ],
  3: [
    { kind: 'skull', scale: 1.2, tall: false, weight: 3 },
    { kind: 'bones', scale: 1.2, tall: false, weight: 3 },
    { kind: 'ember_stone', scale: 1.3, tall: false, weight: 4, light: { color: 0xff7a2f, radius: 90 } },
    { kind: 'dead_tree', scale: 1.6, tall: true, weight: 2 },
    { kind: 'pillar', scale: 1.3, tall: true, weight: 3 },
    { kind: 'cross', scale: 1.2, tall: true, weight: 1 },
  ],
};

export function interTex(type: 'shrine' | 'chest' | 'altar'): Texture {
  const key = 'inter3:' + type;
  if (type === 'shrine')
    return paint(key, 36, 36, (ctx, w, h) => {
      ctx.fillStyle = vgrad(ctx, 0, 14, h, '#6a6f8a', '#2a2c3a');
      ctx.beginPath();
      ctx.moveTo(4, h - 3);
      ctx.lineTo(7, 18);
      ctx.lineTo(w - 7, 18);
      ctx.lineTo(w - 4, h - 3);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#4a4f66';
      ctx.beginPath();
      ctx.ellipse(w / 2, 18, 15, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      glow(ctx, '#ffe38a', 10);
      ctx.fillStyle = '#ffe9a8';
      ctx.beginPath();
      ctx.ellipse(w / 2, 18, 11, 3.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff6d0';
      ctx.fillRect(w / 2 - 1.2, 2, 2.4, 14);
      noGlow(ctx);
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(2, h - 3, w - 4, 3);
    });
  if (type === 'chest')
    return paint(key, 32, 26, (ctx, w, h) => {
      ctx.fillStyle = vgrad(ctx, 0, 6, h, '#7a5a3a', '#3a2818');
      ctx.fillRect(3, 9, w - 6, h - 11);
      ctx.fillStyle = vgrad(ctx, 0, 2, 10, '#9a7a4a', '#5a3e22');
      ctx.beginPath();
      ctx.moveTo(3, 10);
      ctx.quadraticCurveTo(w / 2, -2, w - 3, 10);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#c9a86a';
      ctx.fillRect(3, 9, w - 6, 2);
      ctx.fillRect(8, 3, 2, h - 5);
      ctx.fillRect(w - 10, 3, 2, h - 5);
      glow(ctx, '#ffd166', 8);
      ctx.fillStyle = '#ffe38a';
      ctx.fillRect(w / 2 - 2, 11, 4, 5);
      noGlow(ctx);
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(2, h - 2, w - 4, 2);
    });
  return paint(key, 36, 30, (ctx, w, h) => {
    ctx.fillStyle = vgrad(ctx, 0, 10, h, '#4a3a44', '#1e161c');
    ctx.fillRect(4, 12, w - 8, h - 14);
    ctx.fillStyle = '#5a4a54';
    ctx.fillRect(2, 10, w - 4, 4);
    ctx.fillStyle = '#7a1a2a';
    ctx.beginPath();
    ctx.ellipse(w / 2, 12, 9, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(w / 2 - 5, 12, 2, 8);
    for (const x of [7, w - 8]) {
      ctx.fillStyle = '#e8dcc0';
      ctx.fillRect(x - 1.2, 2, 2.4, 9);
      glow(ctx, '#ffb060', 8);
      ctx.fillStyle = '#ffd08a';
      ctx.beginPath();
      ctx.ellipse(x, 1.5, 1.6, 2.6, 0, 0, Math.PI * 2);
      ctx.fill();
      noGlow(ctx);
    }
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(2, h - 2, w - 4, 2);
  });
}

export function pickupTex(type: 'item' | 'consumable' | 'heal', color: string): Texture {
  const key = `pickup3:${type}:${color}`;
  if (type === 'item')
    return paint(key, 18, 20, (ctx, w, h) => {
      glow(ctx, color, 8);
      ctx.fillStyle = vgrad(ctx, 0, 2, h - 2, shadeHex(color, 1.5), shadeHex(color, 0.7));
      ctx.beginPath();
      ctx.moveTo(w / 2, 1);
      ctx.lineTo(w - 2, h * 0.4);
      ctx.lineTo(w / 2, h - 1);
      ctx.lineTo(2, h * 0.4);
      ctx.closePath();
      ctx.fill();
      noGlow(ctx);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath();
      ctx.moveTo(w / 2, 4);
      ctx.lineTo(w / 2 + 3, h * 0.38);
      ctx.lineTo(w / 2 - 1, h * 0.3);
      ctx.closePath();
      ctx.fill();
    });
  if (type === 'heal')
    return paint(key, 16, 16, (ctx, w, h) => {
      glow(ctx, '#ff5c7a', 8);
      const g = ctx.createRadialGradient(w / 2 - 2, h / 2 - 2, 1, w / 2, h / 2, 7);
      g.addColorStop(0, '#ffd0da');
      g.addColorStop(1, '#c0304f');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, 6.5, 0, Math.PI * 2);
      ctx.fill();
      noGlow(ctx);
    });
  return paint(key, 14, 20, (ctx, w, h) => {
    ctx.fillStyle = '#6a5a4a';
    ctx.fillRect(w / 2 - 2.5, 0, 5, 4);
    glow(ctx, color, 6);
    ctx.fillStyle = vgrad(ctx, 0, 4, h, shadeHex(color, 1.4), shadeHex(color, 0.7));
    ctx.beginPath();
    ctx.moveTo(w / 2 - 2, 4);
    ctx.lineTo(w / 2 + 2, 4);
    ctx.lineTo(w / 2 + 2, 8);
    ctx.quadraticCurveTo(w + 1, h * 0.6, w / 2, h - 1);
    ctx.quadraticCurveTo(-1, h * 0.6, w / 2 - 2, 8);
    ctx.closePath();
    ctx.fill();
    noGlow(ctx);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.ellipse(w / 2 - 2, h * 0.65, 1.5, 3, 0.3, 0, Math.PI * 2);
    ctx.fill();
  });
}

/** Tapered crescent for melee slashes; white, tinted at use, pointing +x. */
export function slashTex(): Texture {
  return paint('slash3', 128, 128, (ctx, w, h) => {
    const cx = w / 2;
    const cy = h / 2;
    const r = 52;
    const a0 = -1.15;
    const a1 = 1.15;
    const steps = 40;
    glow(ctx, '#ffffff', 6);
    for (let i = 0; i < steps; i++) {
      const t0 = i / steps;
      const t1 = (i + 1) / steps;
      const s0 = a0 + (a1 - a0) * t0;
      const s1 = a0 + (a1 - a0) * t1 + 0.02;
      const width = Math.pow(Math.sin(Math.PI * (t0 + t1) / 2), 0.8) * 13;
      ctx.strokeStyle = `rgba(255,255,255,${0.55 + 0.45 * Math.sin(Math.PI * t0)})`;
      ctx.lineWidth = Math.max(0.5, width);
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.arc(cx, cy, r - width * 0.35, s0, s1);
      ctx.stroke();
    }
    noGlow(ctx);
    // bright leading edge
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3, a0 + 0.2, a1 - 0.2);
    ctx.stroke();
  });
}

/** Soft elliptical shadow. */
export function shadowTex(): Texture {
  return paint('shadow3', 32, 16, (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 1, w / 2, h / 2, w / 2);
    g.addColorStop(0, 'rgba(0,0,0,0.55)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

export function heroPortrait(hero: HeroId): string {
  return artDataUrl(buildHero(hero), 128);
}

export function mobPortrait(defId: string): string {
  return artDataUrl(buildMob(defId), 128);
}
